<template>
  <div class="card bva-page">
    <PageHeader title="Budget vs Actual" :showBtn="false"/>
    <div class="bva-panel">
      <div class="row g-3">
        <div class="col-12 col-md-4">
          <label class="bva-label">Master Budget Line <span class="bva-required">*</span></label>
          <v-select
            v-model="budgetLineId"
            placeholder="Select master budget line"
            :options="budgetLines"
            label="name"
            :reduce="option => option.id"
            @update:modelValue="onSelectBudgetLine"
          />
        </div>
        <div class="col-12 col-md-4">
          <label class="bva-label">Budget Allocation</label>
          <v-select
            v-model="budgetMasterId"
            :placeholder="budgetPlaceholder"
            :options="budgets"
            :get-option-label="budgetLabel"
            :reduce="option => option.id"
            :disabled="! budgetLineId"
            @update:modelValue="onSelectBudget"
          />
        </div>
        <div class="col-12 col-md-4">
          <label class="bva-label">Report Type <span class="bva-required">*</span></label>
          <v-select
            v-model="reportType"
            placeholder="Report type"
            :options="reportTypes"
            label="name"
            :reduce="option => option.id"
            :clearable="false"
          />
        </div>
        <div class="col-6 col-xl-3">
          <label class="bva-label">Head Type <span class="bva-required">*</span></label>
          <v-select
            v-model="accountType"
            placeholder="Head type"
            :options="accountTypes"
            label="name"
            :reduce="option => option.id"
          />
        </div>
        <div class="col-6 col-xl-3">
          <label class="bva-label">Interval</label>
          <v-select
            v-model="interval"
            placeholder="Interval"
            :options="intervals"
            label="name"
            :reduce="option => option.id"
            :clearable="false"
            :searchable="false"
            disabled
          />
        </div>
        <div class="col-6 col-xl-3">
          <label class="bva-label">From</label>
          <input v-model="startDate" type="date" class="form-control bva-input" disabled>
        </div>
        <div class="col-6 col-xl-3">
          <label class="bva-label">To</label>
          <input v-model="endDate" type="date" class="form-control bva-input" disabled>
        </div>
      </div>
    </div>
    <div class="bva-footer">
      <div class="bva-footer-info">
        <span v-if="validationMessage" class="bva-notice"><span><i class="fa-solid fa-circle-info"></i></span> {{ validationMessage }}</span>
        <template v-else>
          <span v-for="chip in summaryChips" :key="chip.key" class="bva-chip">{{ chip.label }}: <strong>{{ chip.value }}</strong></span>
        </template>
      </div>
      <div class="bva-footer-actions">
        <button type="button" class="bva-link-btn" :disabled="isExporting" @click="resetForm">Reset</button>
        <button
          type="button"
          class="btn btn-primary bva-btn"
          :disabled="!! validationMessage || isExporting"
          @click="exportReport"
        >
          <span v-if="isExporting" class="spinner-border spinner-border-sm" role="status"></span>
          <span v-else><i class="fa-solid fa-file-excel"></i></span>
          {{ isExporting ? 'Exporting…' : 'Export Report' }}
        </button>
      </div>
    </div>
    <Loader v-if="isLoading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import Loader from '@/components/atom/LoaderComponent'
import handleBudgetTracker from '@/services/modules/procurement/budgetTracker'
import handleDropdowns from '@/services/modules/dropdowns'
import { downloadBlob } from '@/services/utils/global.js'

const { fetchBudgetLines, fetchApprovedBudgets, exportBudgetLineBva } = handleBudgetTracker()
const { fetchDropdownList } = handleDropdowns()

const $route = useRoute()
const showError = inject('showError')

const DEFAULT_ACCOUNT_TYPE = 'account'
const BUDGET_ONLY_TYPE = 'item'
const DEFAULT_REPORT_TYPE = 'budget_vs_actual'
const BUDGET_REPORT_TYPE = 'budget'
const ACTUAL_REPORT_TYPES = ['actual', 'budget_vs_actual_comparison']

const companyQuery = `?company_id=${$route.params.companyId}`
const isLoading = ref(false)
const homeContent = ref({})
const budgetLines = ref([])
const budgets = ref([])
const budgetLineId = ref(null)
const budgetMasterId = ref(null)
const accountType = ref(DEFAULT_ACCOUNT_TYPE)
const reportType = ref(DEFAULT_REPORT_TYPE)
const interval = ref(null)
const startDate = ref('')
const endDate = ref('')
const isExporting = ref(false)

const intervals = computed(() => {
  const projectIntervals = homeContent.value.project_intervals || {}
  return Object.keys(projectIntervals).map(id => ({ id: id, name: projectIntervals[id] }))
})

const accountTypes = computed(() => {
  const types = homeContent.value.budget_head_type || {}
  return Object.keys(types).map(id => ({ id: id, name: types[id] }))
})

const reportTypes = computed(() => {
  const types = homeContent.value.budget_report_types || {}
  return Object.keys(types).map(id => ({ id: id, name: types[id] }))
})

const selectedLine = computed(() => budgetLines.value.find(line => line.id === budgetLineId.value) || null)

const selectedBudget = computed(() => budgets.value.find(budget => budget.id === budgetMasterId.value) || null)

const periodSource = computed(() => selectedBudget.value || selectedLine.value)

const budgetPlaceholder = computed(() => {
  if (! budgetLineId.value) return 'Select a master budget line first'
  if (! budgets.value.length) return 'No approved budget allocation on this line'
  return 'All approved budget allocations'
})

const isBudgetOnly = () => accountType.value === BUDGET_ONLY_TYPE

const effectiveReportType = () => {
  if (isBudgetOnly()) return BUDGET_REPORT_TYPE
  return reportType.value
}

const exportName = () => {
  const match = reportTypes.value.find(option => option.id === effectiveReportType())
  if (! match) return ''
  return match.name
}

const validationMessage = computed(() => {
  if (! budgetLineId.value) return 'Select a master budget line.'
  if (isLoading.value) return 'Loading budget allocations…'
  if (! budgets.value.length) return 'The master budget line has no approved budget allocation.'
  if (! accountType.value) return 'Select a head type.'
  if (! reportType.value) return 'Select a report type.'
  if (isBudgetOnly() && ACTUAL_REPORT_TYPES.includes(reportType.value)) return 'Item heads have no actual. Select Budget report type.'
  if (! interval.value) return 'The selected budget has no interval.'
  if (! startDate.value || ! endDate.value) return 'The selected budget has no From and To dates.'
  if (startDate.value > endDate.value) return 'From date must be on or before To date.'
  return ''
})

const intervalName = () => {
  const match = intervals.value.find(option => option.id === interval.value)
  if (! match) return ''
  return match.name
}

const accountTypeName = () => {
  const match = accountTypes.value.find(option => option.id === accountType.value)
  if (! match) return ''
  return match.name
}

const lineName = () => {
  if (! selectedLine.value) return ''
  return selectedLine.value.name
}

const budgetTypeLabel = (budget) => {
  if (! budget.dimension_name) return budget.budget_type_name
  return `${budget.budget_type_name}: ${budget.dimension_name}`
}

const budgetName = () => {
  if (! selectedBudget.value) return `All (${budgets.value.length})`
  return selectedBudget.value.name
}

const budgetTypeName = () => {
  if (! selectedBudget.value) return 'Budget wise'
  return budgetTypeLabel(selectedBudget.value)
}

const summaryChips = computed(() => [
  { key: 'budget', label: 'Budget Allocation', value: budgetName() },
  { key: 'budget_type', label: 'Budget Type', value: budgetTypeName() },
  { key: 'type', label: 'Head Type', value: accountTypeName() },
  { key: 'report_type', label: 'Report Type', value: exportName() },
  { key: 'period', label: 'Period', value: `${startDate.value} to ${endDate.value}` },
  { key: 'interval', label: 'Interval', value: intervalName() }
])

const budgetCodeName = (budget) => {
  if (! budget.code) return budget.name
  return `${budget.name} (${budget.code})`
}

const budgetLabel = (budget) => `${budgetCodeName(budget)} · ${budgetTypeLabel(budget)}`

const dateOnly = (value) => {
  if (! value) return ''
  return String(value).slice(0, 10)
}

const applyPeriodSource = () => {
  const source = periodSource.value
  if (! source) return
  startDate.value = dateOnly(source.start_date)
  endDate.value = dateOnly(source.end_date)
  interval.value = source.interval || null
}

const onSelectBudget = () => {
  applyPeriodSource()
}

const onSelectBudgetLine = async () => {
  budgets.value = []
  budgetMasterId.value = null
  if (! budgetLineId.value) return
  applyPeriodSource()
  isLoading.value = true
  try {
    const res = await fetchApprovedBudgets(budgetLineId.value, companyQuery)
    if (! res.status) return showError(res.message)
    budgets.value = res.data
  } catch (err) {
    showError(err.message)
  } finally {
    isLoading.value = false
  }
}

const resetForm = () => {
  budgetLineId.value = null
  budgets.value = []
  budgetMasterId.value = null
  accountType.value = DEFAULT_ACCOUNT_TYPE
  reportType.value = DEFAULT_REPORT_TYPE
  interval.value = null
  startDate.value = ''
  endDate.value = ''
}

const reportTitle = () => `${exportName()} Report`

const exportPayload = () => ({
  company_id: $route.params.companyId,
  report_title: `${reportTitle()} - ${accountTypeName()}`,
  account_type: accountType.value,
  report_type: reportType.value,
  budget_master_id: budgetMasterId.value
})

const scopeName = () => {
  if (! selectedBudget.value) return lineName()
  return `${lineName()} - ${selectedBudget.value.name}`
}

const exportFileName = () => {
  const safeName = String(scopeName()).replace(/[\\/]/g, '-')
  return `${exportName()} - ${accountTypeName()} - ${safeName} - ${startDate.value} to ${endDate.value}.xlsx`
}

const saveOrExplain = async (blob, name) => {
  if (! blob.type.includes('json')) {
    downloadBlob(blob, name)
    return
  }
  const response = JSON.parse(await blob.text())
  showError(response.message)
}

const exportReport = async () => {
  if (validationMessage.value) return showError(validationMessage.value)
  if (isExporting.value) return
  isExporting.value = true
  try {
    const response = await exportBudgetLineBva(budgetLineId.value, exportPayload())
    await saveOrExplain(response.data, exportFileName())
  } catch (err) {
    showError('Something went wrong. Please try again.')
  } finally {
    isExporting.value = false
  }
}

const getDropdowns = async () => {
  const res = await fetchDropdownList(companyQuery + '&is_home=1')
  if (! res.status || ! res.data) return
  homeContent.value = res.data.home_data || {}
}

const getBudgetLines = async () => {
  const res = await fetchBudgetLines(companyQuery)
  if (! res.status) return
  budgetLines.value = res.data
}

onMounted(async () => {
  isLoading.value = true
  try {
    await Promise.all([getDropdowns(), getBudgetLines()])
  } catch (err) {
    showError(err.message)
  } finally {
    isLoading.value = false
  }
})
</script>

<style scoped>
.bva-page {
  overflow: visible;
}
.bva-panel {
  padding: 16px 20px;
}
.bva-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475467;
}
.bva-required {
  color: #f04438;
}
.bva-link-btn {
  border: 0;
  padding: 0;
  background: transparent;
  color: #7367f0;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.bva-link-btn:hover {
  color: #5e50ee;
  text-decoration: underline;
}
.bva-link-btn:disabled {
  color: #98a2b3;
  cursor: not-allowed;
  text-decoration: none;
}
.bva-panel :deep(.vs--disabled .vs__search) {
  display: none;
}
.bva-input {
  height: 38px;
  border-radius: 8px;
  border-color: #d0d5dd;
  font-size: 14px;
}
.bva-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid #eaecf0;
  border-radius: 0 0 .5rem .5rem;
  background: #f9fafb;
}
.bva-footer-info {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.bva-chip {
  padding: 4px 10px;
  border: 1px solid #e4e7ec;
  border-radius: 999px;
  background: #fff;
  font-size: 12px;
  color: #667085;
}
.bva-chip strong {
  color: #101828;
  font-weight: 600;
}
.bva-notice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #b54708;
}
.bva-footer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
.bva-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 14px;
  white-space: nowrap;
}
@media (max-width: 576px) {
  .bva-panel {
    padding: 14px 16px;
  }
  .bva-footer {
    padding: 12px 16px;
  }
  .bva-footer-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  .bva-footer-actions .bva-link-btn {
    flex-basis: 100%;
    text-align: left;
  }
  .bva-footer-actions .bva-btn {
    flex: 1;
    min-width: 0;
  }
}
</style>
